import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, Lock, Eye, EyeOff, Loader2, ArrowLeft, PawPrint } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_140853a5-d37e-4724-8f64-c2f7eabb8728/artifacts/4msp2ofk_agrosvetlogo.png';
const CAT_IMAGE = 'https://images.unsplash.com/photo-1681137617814-4048d770fe1b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwxfHxoYXBweSUyMGNhdCUyMHBvcnRyYWl0JTIwc3R1ZGlvfGVufDB8fHx8MTc3Mzg5NjE4NXww&ixlib=rb-4.1.0&q=85';

const CustomerLogin = () => {
  const navigate = useNavigate();
  const { customerLogin } = useAuth();
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await customerLogin(phone, password);
    if (result.success) {
      navigate('/customer-panel');
    } else {
      setError(result.error || 'Telefon numarası veya şifre hatalı');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#4EB8A1] via-[#5CC4AD] to-[#7DD8C4] flex flex-col items-center justify-center p-4 relative overflow-hidden">
      {/* Background decorative circles */}
      <div className="absolute top-[-100px] left-[-60px] w-[350px] h-[350px] bg-white/[0.05] rounded-full" />
      <div className="absolute bottom-[-120px] right-[-80px] w-[450px] h-[450px] bg-white/[0.04] rounded-full" />

      {/* Back to home */}
      <button
        onClick={() => navigate('/')}
        className="absolute top-5 left-5 flex items-center gap-1.5 text-white/70 hover:text-white text-sm transition-colors"
        data-testid="customer-login-back-home"
      >
        <ArrowLeft size={16} /> Ana Sayfa
      </button>

      {/* Login Card */}
      <div className="w-full max-w-[860px] bg-white rounded-3xl shadow-2xl overflow-hidden flex flex-row-reverse relative z-10" data-testid="customer-login-card">
        {/* Right - Image with overlay */}
        <div className="hidden md:block w-[46%] relative">
          <img src={CAT_IMAGE} alt="Pet" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#4EB8A1]/80 via-transparent to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-7">
            <div className="flex items-center gap-2 mb-2">
              <PawPrint size={20} className="text-white/90" />
              <span className="text-white font-bold text-sm">Pet Sahibi Portalı</span>
            </div>
            <p className="text-white/80 text-xs leading-relaxed">
              Hayvanlarınızın sağlık bilgilerini ve randevularını takip edin.
            </p>
          </div>
        </div>

        {/* Left - Form */}
        <div className="flex-1 px-8 sm:px-12 py-10 flex flex-col justify-center">
          {/* Logo */}
          <div className="flex items-center gap-2.5 mb-8">
            <img src={LOGO_URL} alt="AGROSVET" className="w-10 h-10 object-contain" />
            <div>
              <span className="font-extrabold text-gray-900 text-base block leading-tight">AGROSVET</span>
              <span className="text-[10px] text-[#4EB8A1] font-semibold uppercase tracking-widest">Pet Sahibi Girişi</span>
            </div>
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mb-1" data-testid="customer-login-title">
            Merhaba
          </h2>
          <p className="text-sm text-gray-400 mb-7">Hesabınıza giriş yapın</p>

          {error && (
            <div className="bg-red-50 text-red-600 text-sm p-3 rounded-xl text-center mb-5 animate-fade-in" data-testid="customer-login-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">Telefon</label>
              <div className="relative">
                <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#4EB8A1]/50" size={17} />
                <input
                  type="tel"
                  placeholder="05XXXXXXXXX"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 11))}
                  inputMode="numeric"
                  maxLength={11}
                  className="w-full h-12 pl-11 pr-4 text-sm bg-[#F5FAF9] border-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4EB8A1]/30 transition-all placeholder:text-gray-300"
                  required
                  data-testid="customer-login-phone-input"
                />
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">Şifre</label>
              <div className="relative">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#4EB8A1]/50" size={17} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Şifrenizi girin"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full h-12 pl-11 pr-11 text-sm bg-[#F5FAF9] border-0 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4EB8A1]/30 transition-all placeholder:text-gray-300"
                  required
                  data-testid="customer-login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 text-sm font-bold bg-[#4EB8A1] hover:bg-[#3EA892] rounded-full transition-all shadow-lg shadow-[#4EB8A1]/25 mt-2"
              data-testid="customer-login-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 size={16} className="animate-spin mr-2" />
                  Giriş yapılıyor...
                </>
              ) : (
                'Giriş Yap'
              )}
            </Button>
          </form>

          <div className="mt-6 pt-5 border-t border-gray-100 text-center">
            <p className="text-xs text-gray-400 mb-2">Klinik personeli misiniz?</p>
            <button
              onClick={() => navigate('/login')}
              className="text-sm text-[#6b6de3] hover:underline font-semibold"
              data-testid="customer-login-staff-link"
            >
              Personel girişi
            </button>
          </div>
        </div>
      </div>

      <p className="text-center text-xs text-white/40 mt-6 relative z-10">
        &copy; {new Date().getFullYear()} AgrosTech. Tüm hakları saklıdır.
      </p>
    </div>
  );
};

export default CustomerLogin;
